import React from 'react';

import { observer } from 'mobx-react';

import classes from './LeaderboardRowDesktop.module.scss';
import { LeaderboardRowInfo } from '../../../../../interfaces/leaderboard/LeaderboardRowInfo';
import {
  LEADERBOARD_COLUMN,
  LeaderboardColumn,
  RenderColumnInfo,
} from '../../../../../constants/leaderboardColumn';
import {
  GridTableCell,
  GridTableRow,
} from '../../../../../../shared/components/table-components';
import { RankCell } from '../RankCell/RankCell';
import { CustomerCell } from '../CustomerCell/CustomerCell';
import { getNumberWithSignView } from '../../../../../../shared/utils/stringUtils/getNumberWithSignView/getNumberWithSignView';
import { leaderboardStore } from '../../../../../stores/leaderboardStore';
import { getValueOrDash } from '../../../../../../shared/utils/stringUtils/getValueOrDash/getValueOrDash';
import { MuscleMultiplierAdditionalInfo } from '../MuscleMultiplierAdditionalInfo/MuscleMultiplierAdditionalInfo';

type LeaderboardRowDesktopProps = {
  leaderboardRow: LeaderboardRowInfo;
  leaderboardColumns: LeaderboardColumn[];
};

const LeaderboardRowDesktop = observer(
  ({ leaderboardRow, leaderboardColumns }: LeaderboardRowDesktopProps) => {
    const { challengeInfo } = leaderboardStore;
    const { scoringRules } = challengeInfo || {};

    const renderColumnsInfo: RenderColumnInfo = {
      [LEADERBOARD_COLUMN.RANK]: <RankCell rank={leaderboardRow.rank} />,
      [LEADERBOARD_COLUMN.CUSTOMER]: (
        <CustomerCell
          nickname={leaderboardRow.nickname}
          gender={leaderboardRow.gender}
          age={leaderboardRow.age}
        />
      ),
      [LEADERBOARD_COLUMN.TOTAL_SCORE]: (
        <GridTableCell>{getValueOrDash(leaderboardRow.totalScore)}</GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FOOD_TRACKING_PERCENT]: (
        <GridTableCell>
          {getValueOrDash(leaderboardRow.foodTrackingPercentage, '%')}
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FOOD_TRACKING_RANK]: (
        <GridTableCell>{getValueOrDash(leaderboardRow.foodTrackingRank)}</GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_PERCENT]: (
        <GridTableCell>
          {getValueOrDash(leaderboardRow.fatLossPercentage, '%')}
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.ADJUSTED_FAT_LOSS_PERCENT]: (
        <GridTableCell>
          {getValueOrDash(leaderboardRow.adjustedFatLossPercentage, '%')}
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_RANK]: (
        <GridTableCell>{getValueOrDash(leaderboardRow.fatLossRank)}</GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_LBS]: (
        <GridTableCell>{getValueOrDash(leaderboardRow.fatLossLbs)}</GridTableCell>
      ),
      [LEADERBOARD_COLUMN.MUSCLE_GAIN_LBS]: (
        <GridTableCell>
          <>
            {getValueOrDash(getNumberWithSignView(leaderboardRow.muscleGainLbs))}
            &nbsp;
            <MuscleMultiplierAdditionalInfo
              scoringRuleType={scoringRules}
              muscleGainLbs={leaderboardRow.muscleGainLbs}
              muscleGainMultiplier={leaderboardRow.muscleGainMultiplier}
            />
          </>
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.MUSCLE_MULTIPLIER]: (
        <GridTableCell>
          {getValueOrDash(leaderboardRow.muscleGainMultiplier, '*', 'prefix')}
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.ADJUSTED_SCORE_PERCENT]: (
        <GridTableCell>
          {getValueOrDash(leaderboardRow.adjustedFatLossPercentage, '%')}
        </GridTableCell>
      ),
    };

    return (
      <GridTableRow className={classes.row} variant="compactRow">
        {leaderboardColumns.map((column) => (
          <React.Fragment key={column}>{renderColumnsInfo[column]}</React.Fragment>
        ))}
      </GridTableRow>
    );
  },
);

export { LeaderboardRowDesktop };
